import React, { useState, useRef, useEffect } from "react";
import Editor, { Toolbar, BtnBold, BtnItalic, BtnUnderline, BtnBulletList, BtnNumberedList, BtnLink } from "react-simple-wysiwyg";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import MessageBubble from "./MessageBubble";

export default function ConversationView({ thread, messages, loading, onSend, onRelink, onRecheckLabel, sending, templates }) {
  const [draft, setDraft] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [includeSignature, setIncludeSignature] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const scrollRef = useRef(null);

  // Only clear the draft when switching to a different thread — not on every
  // new-message poll, which would otherwise wipe out whatever the admin is
  // still typing whenever someone else's reply (or the next sync tick) lands.
  useEffect(() => {
    setDraft("");
    setCc("");
    setBcc("");
    setShowCcBcc(false);
    setIncludeSignature(true);
  }, [thread?._id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread?._id, messages?.length]);

  if (!thread) {
    return (
      <div className="cinbox-conversation cinbox-conversation-empty">
        <div className="stripe-empty">
          <div className="stripe-empty-icon">💬</div>
          Select a conversation to view messages
        </div>
      </div>
    );
  }

  const isDraftEmpty = !draft.replace(/<[^>]+>/g, "").trim();

  const handleSend = () => {
    if (isDraftEmpty || sending) return;
    onSend(thread._id, draft, {
      cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
      bcc: bcc.split(",").map((s) => s.trim()).filter(Boolean),
      includeSignature,
    });
    setDraft("");
    setCc("");
    setBcc("");
    setShowCcBcc(false);
  };

  const handleInsertTemplate = (template) => {
    setDraft((prev) => (prev && !isDraftEmpty ? `${prev}<br/>${template.bodyHtml}` : template.bodyHtml));
    setShowTemplates(false);
  };

  return (
    <div className="cinbox-conversation">
      <div className="cinbox-conversation-header">
        <div>
          <div className="cinbox-conversation-name">{thread.contactName || thread.contactEmail}</div>
          <div className="cinbox-conversation-email">{thread.contactEmail}</div>
        </div>
        <div className="cinbox-conversation-header-badges">
          {thread.isMatched ? (
            <span className="stripe-badge cinbox-badge-matched">
              <CheckCircleIcon /> Matched to CRM
            </span>
          ) : (
            <span className="stripe-badge cinbox-badge-unmatched">Unmatched</span>
          )}
          <button className="cinbox-recheck-btn" onClick={() => onRecheckLabel(thread._id)} title="Re-check campaign label">
            <RefreshIcon />
          </button>
        </div>
      </div>

      {thread.campaignLabel && (
        <div className="cinbox-campaign-banner">
          <LocalOfferOutlinedIcon />
          <span>
            Likely reply to <strong>{thread.campaignLabel.campaignName}</strong>
            {thread.campaignLabel.labelSource === "manual" ? " (set manually)" : " (best-effort match)"}
          </span>
        </div>
      )}

      {!thread.isMatched && thread.candidateCrmIds?.length > 0 && (
        <div className="cinbox-relink-banner">
          <span>Possible CRM contacts for {thread.contactEmail}:</span>
          <div className="cinbox-relink-options">
            {thread.candidateCrmIds.map((c) => (
              <button key={c._id} className="cinbox-relink-option" onClick={() => onRelink(thread._id, c._id)}>
                {c.contactName || c.name || c.email}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="cinbox-message-scroll" ref={scrollRef}>
        {loading ? (
          <div className="stripe-empty">Loading conversation…</div>
        ) : (
          (messages || []).map((m) => <MessageBubble key={m._id} message={m} />)
        )}
      </div>

      <div className="cinbox-composer">
        {showCcBcc && (
          <div className="cinbox-ccbcc-fields">
            <input type="text" placeholder="Cc (comma-separated)" value={cc} onChange={(e) => setCc(e.target.value)} />
            <input type="text" placeholder="Bcc (comma-separated)" value={bcc} onChange={(e) => setBcc(e.target.value)} />
          </div>
        )}

        <Editor
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          containerProps={{ className: "cinbox-rsw-container" }}
          placeholder="Write a reply…"
        >
          <Toolbar>
            <BtnBold />
            <BtnItalic />
            <BtnUnderline />
            <BtnBulletList />
            <BtnNumberedList />
            <BtnLink />

            <button type="button" className="cinbox-composer-link-btn" onClick={() => setShowCcBcc((v) => !v)}>
              {showCcBcc ? "Hide Cc/Bcc" : "Cc/Bcc"}
            </button>

            <button
              type="button"
              className="cinbox-composer-link-btn"
              onClick={() => setIncludeSignature((v) => !v)}
              title={includeSignature ? "Signature will be added to this reply" : "Signature will not be added to this reply"}
            >
              {includeSignature ? <CheckBoxIcon fontSize="inherit" /> : <CheckBoxOutlineBlankIcon fontSize="inherit" />} Signature
            </button>

            {templates?.length > 0 && (
              <div className="cinbox-templates-dd-wrap">
                <button type="button" className="cinbox-composer-link-btn" onClick={() => setShowTemplates((v) => !v)}>
                  <ArticleOutlinedIcon fontSize="inherit" /> Insert template
                </button>
                {showTemplates && (
                  <div className="cinbox-templates-dd">
                    {templates.map((t) => (
                      <button key={t._id} type="button" className="cinbox-templates-dd-item" onClick={() => handleInsertTemplate(t)}>
                        {t.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button type="button" className="cinbox-send-btn" onClick={handleSend} disabled={isDraftEmpty || sending}>
              <SendIcon /> {sending ? "Sending…" : "Send"}
            </button>
          </Toolbar>
        </Editor>
      </div>
    </div>
  );
}
